import React from 'react';

/**
 * Contractor Rank Badge System
 * 9-level progression with trade-authentic imagery
 * 
 * 1. Broom Pusher - Dusty broom emblem
 * 2. Rookie - Scuffed hardhat
 * 3. Journeyman - Work gloves
 * 4. Pro - Toolbelt buckle
 * 5. Veteran - Weathered work boots
 * 6. Foreman - Clipboard & white hat
 * 7. Concrete Cowboy - Western hardhat with spurs
 * 8. Legend - Golden hammer & laurels
 * 9. Hey, Big Time! - Crown with construction throne
 */

const RankBadge = ({ rank, size = 'md', locked = false, showGlow = false }) => {
    const sizes = {
        xs: 'w-8 h-8',
        sm: 'w-12 h-12',
        md: 'w-16 h-16',
        lg: 'w-24 h-24',
        xl: 'w-32 h-32'
    };

    const sizeClass = sizes[size] || sizes.md;
    const lockedClass = locked ? 'opacity-40 grayscale' : '';
    const glowClass = showGlow && !locked ? 'drop-shadow-[0_0_12px_rgba(185,28,28,0.6)]' : '';

    const badges = {
        'Broom Pusher': <BroomPusherBadge />,
        'Rookie': <RookieBadge />,
        'Journeyman': <JourneymanBadge />,
        'Pro': <ProBadge />,
        'Veteran': <VeteranBadge />,
        'Foreman': <ForemanBadge />,
        'Concrete Cowboy': <ConcreteCowboyBadge />,
        'Legend': <LegendBadge />,
        'Hey, Big Time!': <BigTimeBadge />,
        // Legacy fallbacks
        'Apprentice': <BroomPusherBadge />,
        'Tool Fetcher': <RookieBadge />,
        'Greenhorn': <JourneymanBadge />,
        'Good Hand': <ProBadge />,
        'Top Hand': <VeteranBadge />,
        'Crew Boss': <ForemanBadge />,
        'Shot Caller': <LegendBadge />,
        'Jobsite Legend': <LegendBadge />,
        'Trades Guardian': <BigTimeBadge />
    };

    return (
        <div className={`${sizeClass} ${lockedClass} ${glowClass} transition-all duration-300`}>
            {badges[rank] || <BroomPusherBadge />}
        </div>
    );
};

// Badge 1: Broom Pusher - Dusty broom on concrete background
const BroomPusherBadge = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
            <linearGradient id="concrete" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6B7280"/>
                <stop offset="100%" stopColor="#4B5563"/>
            </linearGradient>
        </defs>
        {/* Concrete base */}
        <circle cx="50" cy="50" r="45" fill="url(#concrete)" stroke="#9CA3AF" strokeWidth="3"/>
        {/* Inner circle */}
        <circle cx="50" cy="50" r="38" fill="#374151"/>
        {/* Broom handle */}
        <rect x="47" y="15" width="6" height="55" fill="#92400E" rx="2"/>
        {/* Broom head */}
        <path d="M35,70 L65,70 L60,85 L40,85 Z" fill="#FCD34D"/>
        {/* Bristles */}
        <line x1="38" y1="72" x2="36" y2="83" stroke="#D97706" strokeWidth="2"/>
        <line x1="43" y1="72" x2="42" y2="84" stroke="#D97706" strokeWidth="2"/>
        <line x1="48" y1="72" x2="48" y2="85" stroke="#D97706" strokeWidth="2"/>
        <line x1="53" y1="72" x2="53" y2="85" stroke="#D97706" strokeWidth="2"/>
        <line x1="58" y1="72" x2="59" y2="84" stroke="#D97706" strokeWidth="2"/>
        <line x1="63" y1="72" x2="65" y2="83" stroke="#D97706" strokeWidth="2"/>
        {/* Dust particles */}
        <circle cx="30" cy="78" r="2" fill="#9CA3AF" opacity="0.6"/>
        <circle cx="72" cy="75" r="1.5" fill="#9CA3AF" opacity="0.5"/>
        <circle cx="25" cy="72" r="1" fill="#9CA3AF" opacity="0.4"/>
    </svg>
);

// Badge 2: Rookie - Scuffed yellow hardhat
const RookieBadge = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
            <linearGradient id="rookieShield" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#374151"/>
                <stop offset="100%" stopColor="#1F2937"/>
            </linearGradient>
        </defs>
        {/* Shield base */}
        <path d="M50,5 L90,25 L90,60 L50,95 L10,60 L10,25 Z" fill="url(#rookieShield)" stroke="#4B5563" strokeWidth="3"/>
        {/* Inner shield */}
        <path d="M50,12 L82,28 L82,56 L50,86 L18,56 L18,28 Z" fill="#111827"/>
        {/* Hardhat dome */}
        <ellipse cx="50" cy="45" rx="28" ry="18" fill="#FCD34D"/>
        <ellipse cx="50" cy="40" rx="24" ry="14" fill="#FBBF24"/>
        {/* Hardhat brim */}
        <ellipse cx="50" cy="50" rx="32" ry="7" fill="#F59E0B"/>
        {/* Ridge */}
        <rect x="47" y="26" width="6" height="18" fill="#F59E0B" rx="2"/>
        {/* Scuff marks - shows use */}
        <path d="M32,42 Q35,45 33,48" stroke="#92400E" strokeWidth="1.5" fill="none"/>
        <path d="M65,38 L68,42" stroke="#92400E" strokeWidth="1" fill="none"/>
        {/* Rookie star */}
        <path d="M50,62 L52,68 L58,68 L53,72 L55,78 L50,74 L45,78 L47,72 L42,68 L48,68 Z" fill="#9CA3AF"/>
    </svg>
);

// Badge 3: Journeyman - Worn leather work gloves
const JourneymanBadge = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
            <linearGradient id="journeyGreen" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#065F46"/>
                <stop offset="100%" stopColor="#064E3B"/>
            </linearGradient>
        </defs>
        {/* Shield */}
        <path d="M50,5 L90,25 L90,60 L50,95 L10,60 L10,25 Z" fill="url(#journeyGreen)" stroke="#10B981" strokeWidth="3"/>
        <path d="M50,12 L82,28 L82,56 L50,86 L18,56 L18,28 Z" fill="#022C22"/>
        {/* Work glove */}
        <path 
            d="M32,72 L32,48 L36,42 L36,32 L42,32 L42,40 L46,36 L46,26 L52,26 L52,36 L56,36 L56,24 L62,24 L62,38 L66,42 L66,32 L72,32 L72,52 L76,58 L76,72 L60,84 L44,84 Z" 
            fill="#B45309"
            stroke="#78350F"
            strokeWidth="2"
        />
        {/* Glove cuff with stitching */}
        <path d="M32,72 L76,72 L72,82 L60,86 L44,86 L32,82 Z" fill="#92400E"/>
        <path d="M36,74 L40,74 M44,74 L48,74 M52,74 L56,74 M60,74 L64,74" stroke="#78350F" strokeWidth="1"/>
        {/* Wear marks */}
        <circle cx="48" cy="50" r="3" fill="#78350F" opacity="0.5"/>
        <circle cx="58" cy="45" r="2" fill="#78350F" opacity="0.4"/>
        {/* Callus indicator */}
        <path d="M40,55 Q50,58 64,54" stroke="#78350F" strokeWidth="1.5" fill="none"/>
    </svg>
);

// Badge 4: Pro - Heavy-duty toolbelt buckle
const ProBadge = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
            <linearGradient id="proBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1E40AF"/>
                <stop offset="100%" stopColor="#1E3A8A"/>
            </linearGradient>
            <linearGradient id="chrome" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#E5E7EB"/>
                <stop offset="50%" stopColor="#9CA3AF"/>
                <stop offset="100%" stopColor="#6B7280"/>
            </linearGradient>
        </defs>
        {/* Outer ring */}
        <circle cx="50" cy="50" r="46" fill="url(#proBlue)" stroke="#3B82F6" strokeWidth="3"/>
        {/* Inner circle */}
        <circle cx="50" cy="50" r="38" fill="#0F172A"/>
        {/* Belt buckle frame */}
        <rect x="25" y="30" width="50" height="40" rx="4" fill="url(#chrome)" stroke="#6B7280" strokeWidth="2"/>
        {/* Inner buckle */}
        <rect x="30" y="35" width="40" height="30" rx="2" fill="#374151"/>
        {/* PRO text */}
        <text x="50" y="56" textAnchor="middle" fill="#3B82F6" fontSize="16" fontWeight="bold" fontFamily="Arial Black">PRO</text>
        {/* Belt holes */}
        <circle cx="18" cy="50" r="3" fill="#1F2937" stroke="#6B7280"/>
        <circle cx="82" cy="50" r="3" fill="#1F2937" stroke="#6B7280"/>
        {/* Chevron accents */}
        <path d="M32,25 L38,20 L44,25" stroke="#3B82F6" strokeWidth="2" fill="none"/>
        <path d="M56,25 L62,20 L68,25" stroke="#3B82F6" strokeWidth="2" fill="none"/>
    </svg>
);

// Badge 5: Veteran - Weathered steel-toe work boots
const VeteranBadge = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
            <linearGradient id="vetBrown" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#78350F"/>
                <stop offset="100%" stopColor="#451A03"/>
            </linearGradient>
        </defs>
        {/* Decorative outer ring with rivets */}
        <circle cx="50" cy="50" r="46" fill="none" stroke="#D97706" strokeWidth="3"/>
        {[0, 60, 120, 180, 240, 300].map((angle, i) => (
            <circle 
                key={i}
                cx={50 + 46 * Math.cos(angle * Math.PI / 180)} 
                cy={50 + 46 * Math.sin(angle * Math.PI / 180)} 
                r="4" 
                fill="#B45309"
                stroke="#78350F"
                strokeWidth="1"
            />
        ))}
        {/* Inner shield */}
        <path d="M50,8 L85,25 L88,55 L75,75 L50,92 L25,75 L12,55 L15,25 Z" fill="url(#vetBrown)" stroke="#D97706" strokeWidth="2"/>
        <path d="M50,15 L78,28 L80,52 L70,68 L50,82 L30,68 L20,52 L22,28 Z" fill="#292524"/>
        {/* Work boot */}
        <path d="M30,50 L30,70 L45,75 L70,75 L75,65 L75,50 L65,45 L45,45 Z" fill="#57534E"/>
        {/* Steel toe cap */}
        <path d="M65,50 L75,50 L75,65 L70,68 L65,65 Z" fill="#9CA3AF"/>
        {/* Boot sole */}
        <path d="M28,70 L30,75 L45,80 L72,80 L78,72 L75,65 L70,75 L45,75 L30,70 Z" fill="#1C1917"/>
        {/* Laces */}
        <line x1="40" y1="48" x2="55" y2="48" stroke="#78350F" strokeWidth="2"/>
        <line x1="38" y1="54" x2="57" y2="54" stroke="#78350F" strokeWidth="2"/>
        <line x1="36" y1="60" x2="58" y2="60" stroke="#78350F" strokeWidth="2"/>
        {/* Scuff marks */}
        <path d="M68,55 L72,58" stroke="#44403C" strokeWidth="1"/>
        <path d="M70,62 L73,60" stroke="#44403C" strokeWidth="1"/>
    </svg>
);

// Badge 6: Foreman - White hardhat with clipboard
const ForemanBadge = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
            <linearGradient id="foremanPurple" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#5B21B6"/>
                <stop offset="100%" stopColor="#4C1D95"/>
            </linearGradient>
        </defs>
        {/* Outer decorative border */}
        <circle cx="50" cy="50" r="47" fill="none" stroke="#7C3AED" strokeWidth="2"/>
        <circle cx="50" cy="50" r="44" fill="none" stroke="#8B5CF6" strokeWidth="1"/>
        {/* Shield */}
        <path d="M50,5 L92,22 L92,55 L75,78 L50,95 L25,78 L8,55 L8,22 Z" fill="url(#foremanPurple)" stroke="#7C3AED" strokeWidth="2"/>
        <path d="M50,12 L84,26 L84,52 L70,72 L50,86 L30,72 L16,52 L16,26 Z" fill="#2E1065"/>
        {/* White boss hardhat */}
        <ellipse cx="50" cy="38" rx="24" ry="14" fill="#F9FAFB"/>
        <ellipse cx="50" cy="34" rx="20" ry="10" fill="#F3F4F6"/>
        <ellipse cx="50" cy="44" rx="26" ry="5" fill="#E5E7EB"/>
        {/* Purple stripes - boss designation */}
        <rect x="36" y="28" width="28" height="3" fill="#7C3AED"/>
        {/* Ridge */}
        <rect x="48" y="22" width="4" height="14" fill="#E5E7EB" rx="1"/>
        {/* Clipboard */}
        <rect x="36" y="52" width="28" height="34" fill="#78350F" rx="2"/>
        <rect x="38" y="54" width="24" height="30" fill="#FEF3C7"/>
        {/* Clipboard clip */}
        <rect x="44" y="50" width="12" height="6" fill="#9CA3AF" rx="1"/>
        {/* Checklist */}
        <rect x="41" y="58" width="4" height="4" stroke="#7C3AED" strokeWidth="1" fill="none"/>
        <path d="M42,60 L43,62 L46,58" stroke="#22C55E" strokeWidth="1.5" fill="none"/>
        <line x1="48" y1="60" x2="58" y2="60" stroke="#374151" strokeWidth="1"/>
        <rect x="41" y="66" width="4" height="4" stroke="#7C3AED" strokeWidth="1" fill="none"/>
        <path d="M42,68 L43,70 L46,66" stroke="#22C55E" strokeWidth="1.5" fill="none"/>
        <line x1="48" y1="68" x2="56" y2="68" stroke="#374151" strokeWidth="1"/>
        <rect x="41" y="74" width="4" height="4" stroke="#7C3AED" strokeWidth="1" fill="none"/>
        <line x1="48" y1="76" x2="54" y2="76" stroke="#374151" strokeWidth="1"/>
    </svg>
);

// Badge 7: Concrete Cowboy - Western hardhat with attitude
const ConcreteCowboyBadge = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
            <linearGradient id="cowboyOrange" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#C2410C"/>
                <stop offset="100%" stopColor="#7C2D12"/>
            </linearGradient>
            <linearGradient id="sunset" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#F97316"/>
                <stop offset="100%" stopColor="#DC2626"/>
            </linearGradient>
        </defs>
        {/* Sunset background */}
        <circle cx="50" cy="50" r="46" fill="url(#sunset)"/>
        {/* Inner dark circle */}
        <circle cx="50" cy="50" r="40" fill="#1C1917"/>
        {/* Cowboy hardhat - hybrid western/construction */}
        <ellipse cx="50" cy="52" rx="35" ry="10" fill="#78716C"/>
        {/* Hat crown */}
        <path d="M22,52 Q28,30 50,25 Q72,30 78,52" fill="#57534E" stroke="#78716C" strokeWidth="1"/>
        <ellipse cx="50" cy="38" rx="20" ry="12" fill="#44403C"/>
        {/* Western crease */}
        <path d="M35,35 Q50,42 65,35" stroke="#78716C" strokeWidth="2" fill="none"/>
        {/* Hat band - leather with silver conchos */}
        <rect x="30" y="44" width="40" height="5" fill="#92400E"/>
        <circle cx="38" cy="46.5" r="2" fill="#E5E7EB"/>
        <circle cx="50" cy="46.5" r="2" fill="#E5E7EB"/>
        <circle cx="62" cy="46.5" r="2" fill="#E5E7EB"/>
        {/* Star badge on hat */}
        <path d="M50,32 L51.5,36 L56,36 L52.5,39 L54,43 L50,40 L46,43 L47.5,39 L44,36 L48.5,36 Z" fill="#FCD34D"/>
        {/* Spurs at bottom */}
        <path d="M25,80 L15,75 L20,72 L28,78 Z" fill="#E5E7EB"/>
        <circle cx="15" cy="75" r="3" fill="#D1D5DB"/>
        <path d="M75,80 L85,75 L80,72 L72,78 Z" fill="#E5E7EB"/>
        <circle cx="85" cy="75" r="3" fill="#D1D5DB"/>
        {/* Lasso accent */}
        <ellipse cx="50" cy="70" rx="12" ry="6" fill="none" stroke="#D4A574" strokeWidth="2"/>
    </svg>
);

// Badge 8: Legend - Golden hammer with laurels
const LegendBadge = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
            <linearGradient id="legendGold" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FCD34D"/>
                <stop offset="50%" stopColor="#F59E0B"/>
                <stop offset="100%" stopColor="#D97706"/>
            </linearGradient>
            <radialGradient id="legendGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FCD34D" stopOpacity="0.4"/>
                <stop offset="100%" stopColor="#FCD34D" stopOpacity="0"/>
            </radialGradient>
        </defs>
        {/* Glow effect */}
        <circle cx="50" cy="50" r="48" fill="url(#legendGlow)"/>
        {/* Laurel wreath left */}
        <path d="M18,50 Q12,35 22,22 Q28,28 24,38 Q20,34 18,42" fill="#22C55E"/>
        <path d="M18,50 Q12,65 22,78 Q28,72 24,62 Q20,66 18,58" fill="#22C55E"/>
        {/* Laurel wreath right */}
        <path d="M82,50 Q88,35 78,22 Q72,28 76,38 Q80,34 82,42" fill="#22C55E"/>
        <path d="M82,50 Q88,65 78,78 Q72,72 76,62 Q80,66 82,58" fill="#22C55E"/>
        {/* Gold outer ring */}
        <circle cx="50" cy="50" r="42" fill="none" stroke="url(#legendGold)" strokeWidth="5"/>
        {/* Main shield */}
        <path d="M50,10 L85,28 L85,58 L50,90 L15,58 L15,28 Z" fill="#7F1D1D" stroke="#FCD34D" strokeWidth="2"/>
        <path d="M50,16 L78,32 L78,55 L50,82 L22,55 L22,32 Z" fill="#450A0A"/>
        {/* Golden hammer */}
        <rect x="46" y="35" width="8" height="35" fill="url(#legendGold)" rx="2"/>
        {/* Hammer head */}
        <rect x="35" y="28" width="30" height="14" fill="url(#legendGold)" rx="2"/>
        <rect x="35" y="30" width="30" height="4" fill="#B45309"/>
        {/* Claw end */}
        <path d="M35,28 L30,22 L33,20 L38,26" fill="url(#legendGold)"/>
        <path d="M35,42 L30,48 L33,50 L38,44" fill="url(#legendGold)"/>
        {/* Stars */}
        <circle cx="50" cy="78" r="3" fill="#FCD34D"/>
        <circle cx="40" cy="74" r="2" fill="#FCD34D" opacity="0.7"/>
        <circle cx="60" cy="74" r="2" fill="#FCD34D" opacity="0.7"/>
    </svg>
);

// Badge 9: Hey, Big Time! - Ultimate prestige with crown
const BigTimeBadge = () => (
    <svg viewBox="0 0 100 100" className="w-full h-full">
        <defs>
            <linearGradient id="bigTimeGold" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#FEF3C7"/>
                <stop offset="30%" stopColor="#FCD34D"/>
                <stop offset="70%" stopColor="#F59E0B"/>
                <stop offset="100%" stopColor="#D97706"/>
            </linearGradient>
            <radialGradient id="bigTimeGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#FCD34D" stopOpacity="0.5"/>
                <stop offset="70%" stopColor="#F59E0B" stopOpacity="0.2"/>
                <stop offset="100%" stopColor="#D97706" stopOpacity="0"/>
            </radialGradient>
            <filter id="crownGlow">
                <feGaussianBlur stdDeviation="2" result="blur"/>
                <feMerge>
                    <feMergeNode in="blur"/>
                    <feMergeNode in="SourceGraphic"/>
                </feMerge>
            </filter>
        </defs>
        {/* Animated glow background */}
        <circle cx="50" cy="50" r="49" fill="url(#bigTimeGlow)"/>
        {/* Ornate outer ring */}
        <circle cx="50" cy="50" r="46" fill="none" stroke="url(#bigTimeGold)" strokeWidth="4"/>
        {/* Diamond accents around edge */}
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
            <path
                key={i}
                d={`M${50 + 44 * Math.cos((angle) * Math.PI / 180)},${50 + 44 * Math.sin((angle) * Math.PI / 180) - 4} 
                    L${50 + 44 * Math.cos((angle) * Math.PI / 180) + 4},${50 + 44 * Math.sin((angle) * Math.PI / 180)} 
                    L${50 + 44 * Math.cos((angle) * Math.PI / 180)},${50 + 44 * Math.sin((angle) * Math.PI / 180) + 4}
                    L${50 + 44 * Math.cos((angle) * Math.PI / 180) - 4},${50 + 44 * Math.sin((angle) * Math.PI / 180)} Z`}
                fill="#FEF3C7"
            />
        ))}
        {/* Royal purple base */}
        <path d="M50,12 L88,30 L88,58 L50,88 L12,58 L12,30 Z" fill="#581C87" stroke="#A855F7" strokeWidth="2"/>
        <path d="M50,18 L82,34 L82,55 L50,82 L18,55 L18,34 Z" fill="#3B0764"/>
        {/* Majestic Crown */}
        <g filter="url(#crownGlow)">
            <path d="M25,50 L30,30 L38,42 L50,25 L62,42 L70,30 L75,50 L68,52 L50,48 L32,52 Z" fill="url(#bigTimeGold)"/>
            {/* Crown jewels */}
            <circle cx="50" cy="32" r="4" fill="#DC2626"/>
            <circle cx="38" cy="38" r="3" fill="#3B82F6"/>
            <circle cx="62" cy="38" r="3" fill="#22C55E"/>
            {/* Crown points */}
            <circle cx="30" cy="32" r="2" fill="#FEF3C7"/>
            <circle cx="50" cy="27" r="2" fill="#FEF3C7"/>
            <circle cx="70" cy="32" r="2" fill="#FEF3C7"/>
        </g>
        {/* Crown base band */}
        <rect x="28" y="48" width="44" height="6" fill="#D97706"/>
        <rect x="28" y="50" width="44" height="2" fill="#92400E"/>
        {/* "BIG TIME" banner */}
        <path d="M20,65 L50,58 L80,65 L75,75 L50,70 L25,75 Z" fill="#B91C1C"/>
        <text x="50" y="70" textAnchor="middle" fill="#FEF3C7" fontSize="8" fontWeight="bold" fontFamily="Arial Black">BIG TIME!</text>
        {/* Radiating lines */}
        <line x1="50" y1="5" x2="50" y2="10" stroke="#FCD34D" strokeWidth="2"/>
        <line x1="85" y1="20" x2="90" y2="18" stroke="#FCD34D" strokeWidth="2"/>
        <line x1="15" y1="20" x2="10" y2="18" stroke="#FCD34D" strokeWidth="2"/>
    </svg>
);

export default RankBadge;
export { 
    BroomPusherBadge,
    RookieBadge,
    JourneymanBadge,
    ProBadge,
    VeteranBadge,
    ForemanBadge,
    ConcreteCowboyBadge,
    LegendBadge,
    BigTimeBadge
};
